// DYMO Connect Web Service API
// Works with modern browsers - no plugins required
// Requires DYMO Connect software installed and running

const DYMO_CONNECT_URL = 'https://localhost:41951';

export interface DymoConnectPrinter {
  name: string;
  modelName: string;
  isConnected: boolean;
  isLocal: boolean;
}

export interface DymoConnectStatus {
  isConnected: boolean;
  printers: DymoConnectPrinter[];
}

// Check if DYMO Connect service is running
export async function checkDymoConnectStatus(): Promise<DymoConnectStatus> {
  try {
    const response = await fetch(`${DYMO_CONNECT_URL}/DYMO/DLS/Printing/StatusConnected`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      return { isConnected: false, printers: [] };
    }

    const isConnected = await response.json();

    if (isConnected) {
      const printers = await getPrinters();
      return { isConnected: true, printers };
    }

    return { isConnected: false, printers: [] };
  } catch (error) {
    console.log('DYMO Connect not detected:', error);
    return { isConnected: false, printers: [] };
  }
}

// Parse XML printer data
function parsePrintersXml(xmlString: string): DymoConnectPrinter[] {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

  const printers: DymoConnectPrinter[] = [];
  const printerNodes = xmlDoc.querySelectorAll('LabelWriterPrinter');

  printerNodes.forEach((node) => {
    const name = node.querySelector('Name')?.textContent || '';
    const modelName = node.querySelector('ModelName')?.textContent || '';
    const isConnected = node.querySelector('IsConnected')?.textContent === 'True';
    const isLocal = node.querySelector('IsLocal')?.textContent === 'True';

    if (name) {
      printers.push({
        name,
        modelName,
        isConnected,
        isLocal,
      });
    }
  });

  return printers;
}

// Get list of DYMO printers
export async function getPrinters(): Promise<DymoConnectPrinter[]> {
  try {
    const response = await fetch(`${DYMO_CONNECT_URL}/DYMO/DLS/Printing/GetPrinters`, {
      method: 'GET',
    });

    if (!response.ok) {
      console.error('Failed to get printers:', response.statusText);
      return [];
    }

    const xmlText = await response.text();
    console.log('DYMO printers XML:', xmlText);

    const printers = parsePrintersXml(xmlText);
    console.log('Parsed printers:', printers);
    return printers;
  } catch (error) {
    console.error('Error getting printers:', error);
    return [];
  }
}

// Print label using DYMO Connect
export async function printLabel(
  printerName: string,
  labelXml: string,
  copies: number = 1
): Promise<{ success: boolean; error?: string }> {
  try {
    console.log('Sending print request to:', `${DYMO_CONNECT_URL}/DYMO/DLS/Printing/PrintLabel`);
    console.log('Printer name:', printerName);
    console.log('Copies:', copies);
    console.log('Label XML length:', labelXml.length);

    // Print parameters
    const printParamsXml = copies > 1 ? `<?xml version="1.0" encoding="utf-8"?>
<LabelWriterPrintParams>
  <Copies>${copies}</Copies>
</LabelWriterPrintParams>` : '';

    const response = await fetch(`${DYMO_CONNECT_URL}/DYMO/DLS/Printing/PrintLabel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        printerName: printerName,
        printParamsXml: printParamsXml,
        labelXml: labelXml,
        labelSetXml: '',
      }),
    });

    const responseText = await response.text();
    console.log('Print response status:', response.status);
    console.log('Print response:', responseText);

    if (!response.ok) {
      console.error('Print failed - Status:', response.status);
      console.error('Print failed - Response:', responseText);
      return { success: false, error: `HTTP ${response.status}: ${responseText}` };
    }

    console.log('Label sent to printer successfully');
    return { success: true };
  } catch (error) {
    console.error('Error printing label:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

// Print preview (returns base64 PNG image)
export async function renderLabel(
  printerName: string,
  labelXml: string
): Promise<string | null> {
  try {
    const response = await fetch(`${DYMO_CONNECT_URL}/DYMO/DLS/Printing/RenderLabel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        printerName: printerName,
        renderParamsXml: '',
        labelXml: labelXml,
      }),
    });

    if (!response.ok) {
      console.error('Render failed:', response.statusText);
      return null;
    }

    const base64Image = await response.text();
    return base64Image;
  } catch (error) {
    console.error('Error rendering label:', error);
    return null;
  }
}
